import { HtmlAnchorTarget, isNil, Nillable } from '@web/utils';
import { TableItemActionType } from '../enums/table-item-action-type';
import { TableItemAction } from '../models/table-item-action';
import { TableItemActionCommand } from '../types/table-item-action-command';
import { TableItemActionRouterLink } from '../types/table-item-action-router-link';
import { TableItemActionUrl } from '../types/table-item-action-url';
import { TableItemActionVisibility } from '../types/table-item-action-visibility';
import { TableItemActionQueryParams } from '../types/table-item-action-query-params';
import { Icon } from '../../icon/enums/icon';
import { RequiredMethodCallException, RouteProviderService } from '@web/common';
import { TableItemActionSeverity } from '../types/table-item-action-severity';

export class TableItemActionBuilder<Item, Action> {
  private _key: Nillable<Action>;
  private _type: Nillable<TableItemActionType>;
  private _children: Nillable<Array<TableItemAction<Item, Action>>>;
  private _tooltip: Nillable<string>;
  private _severity: Nillable<TableItemActionSeverity>;
  private _command: Nillable<TableItemActionCommand<Item>>;
  private _routerLink: Nillable<TableItemActionRouterLink<Item>>;
  private _url: Nillable<TableItemActionUrl<Item>>;
  private _icon: Nillable<Icon>;
  private _visibility: Nillable<TableItemActionVisibility<Item>>;
  private _queryParams: Nillable<TableItemActionQueryParams<Item>>;
  private _target: Nillable<HtmlAnchorTarget>;

  public constructor(private readonly provider: RouteProviderService) {
    this.reset();
  }

  public newInstance(): TableItemActionBuilder<Item, Action> {
    return new TableItemActionBuilder<Item, Action>(this.provider);
  }

  public from(
    model: TableItemAction<Item, Action>,
  ): TableItemActionBuilder<Item, Action> {
    const {
      key,
      tooltip,
      severity,
      target,
      type,
      icon,
      url,
      command,
      visibility,
      children,
      routerLink,
      queryParams,
    } = model;

    const builder = this.newInstance();

    builder._key = key;
    builder._tooltip = tooltip;
    builder._severity = severity;
    builder._target = target;
    builder._type = type;
    builder._icon = icon;
    builder._url = url;
    builder._command = command;
    builder._visibility = visibility;
    builder._children = children;
    builder._routerLink = routerLink;
    builder._queryParams = queryParams;

    return builder;
  }

  public reset(): this {
    this._key = null;
    this._type = null;
    this._children = null;
    this._tooltip = null;
    this._severity = null;
    this._command = null;
    this._routerLink = null;
    this._url = null;
    this._icon = null;
    this._visibility = null;
    this._queryParams = null;
    this._target = null;

    this.configureDefaultVisibility();

    return this;
  }

  public build(): TableItemAction<Item, Action> {
    this.requireKey();
    this.requireType();
    this.requireTooltip();
    this.requireIcon();

    if (this.isMoreType()) {
      this.requireChildren();
    }

    if (this.isCommandType()) {
      this.requireCommand();
    }

    if (this.isUrlType()) {
      this.requireUrl();

      this.configureDefaultTarget();
    }

    if (this.isRouterLinkType()) {
      this.requireRouterLink();

      this.configureDefaultTarget();
    }

    const action = new TableItemAction(
      this._key as Action,
      this._type as TableItemActionType,
      this._tooltip as string,
      this._icon as Icon,
      this._visibility as TableItemActionVisibility<Item>,
      this._children,
      this._command,
      this._routerLink,
      this._queryParams,
      this._url,
      this._target,
      this._severity,
    );

    this.reset();

    return action;
  }

  public initRouterLink(key: Action): this {
    return this.key(key).type(TableItemActionType.RouterLink);
  }

  public initUrl(key: Action): this {
    return this.key(key).type(TableItemActionType.Url);
  }

  public initMore(key: Action): this {
    return this.key(key)
      .type(TableItemActionType.More)
      .tooltip('Więcej')
      .icon(Icon.ThreeDotsVertical);
  }

  public initCommand(key: Action): this {
    return this.key(key).type(TableItemActionType.Command);
  }

  public command(factory: TableItemActionCommand<Item>): this {
    this._command = factory;

    return this;
  }

  public key(key: Action): this {
    this._key = key;

    return this;
  }

  public tooltip(tooltip: string): this {
    this._tooltip = tooltip;

    return this;
  }

  public severity(severity: TableItemActionSeverity): this {
    this._severity = severity;

    return this;
  }

  public children(
    factory: (
      builder: TableItemActionBuilder<Item, Action>,
    ) => TableItemAction<Item, Action>[],
  ): this {
    this._children = factory(this.newInstance());

    return this;
  }

  public type(type: TableItemActionType): this {
    this._type = type;

    return this;
  }

  public routerLink(
    factory: (
      provider: RouteProviderService,
    ) => TableItemActionRouterLink<Item>,
  ): this {
    this._routerLink = factory(this.provider);

    return this;
  }

  public url(factory: TableItemActionUrl<Item>): this {
    this._url = factory;

    return this;
  }

  public icon(icon: Icon): this {
    this._icon = icon;

    return this;
  }

  public visibility(factory: TableItemActionVisibility<Item>): this {
    this._visibility = factory;

    return this;
  }

  public queryParams(factory: TableItemActionQueryParams<Item>): this {
    this._queryParams = factory;

    return this;
  }

  public target(target: HtmlAnchorTarget): this {
    this._target = target;

    return this;
  }

  private requireKey(): void {
    if (isNil(this._key)) {
      throw new RequiredMethodCallException(this.key.name);
    }
  }

  private requireType(): void {
    if (isNil(this._type)) {
      throw new RequiredMethodCallException(this.type.name);
    }
  }

  private requireChildren(): void {
    if (isNil(this._children)) {
      throw new RequiredMethodCallException(this.children.name);
    }
  }

  private requireTooltip(): void {
    if (isNil(this._tooltip)) {
      throw new RequiredMethodCallException(this.tooltip.name);
    }
  }

  private requireCommand(): void {
    if (isNil(this._command)) {
      throw new RequiredMethodCallException(this.command.name);
    }
  }

  private requireUrl(): void {
    if (isNil(this._url)) {
      throw new RequiredMethodCallException(this.url.name);
    }
  }

  private requireRouterLink(): void {
    if (isNil(this._routerLink)) {
      throw new RequiredMethodCallException(this.routerLink.name);
    }
  }

  private requireIcon(): void {
    if (isNil(this._icon)) {
      throw new RequiredMethodCallException(this.icon.name);
    }
  }

  private isMoreType(): boolean {
    return this._type === TableItemActionType.More;
  }

  private isUrlType(): boolean {
    return this._type === TableItemActionType.Url;
  }

  private isRouterLinkType(): boolean {
    return this._type === TableItemActionType.RouterLink;
  }

  private isCommandType(): boolean {
    return this._type === TableItemActionType.Command;
  }

  private configureDefaultVisibility(): void {
    this.visibility(() => true);
  }

  private configureDefaultTarget(): void {
    if (!isNil(this._target)) {
      return;
    }

    this.target(HtmlAnchorTarget.Self);
  }
}
